import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { parse } from "date-fns";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { Endpoints } from "../../../Global/Endpoints";
import { useTranslation } from 'react-i18next';
import ErrorModal from "../../Security/ErrorModal";
import { useNavigate } from "react-router-dom";

am4core.useTheme(am4themes_animated.default);

const Fertility_Trends = () => {
  const { t } = useTranslation();
  const userData = useSelector((state) => state.userData);
  const token = localStorage.getItem("AccessToken");
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("Hormones");
  const [filteredFields, setFilteredFields] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0); // <-- add this
  const navigate = useNavigate();
  const chartDivRef = useRef(null);

  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";
  
  const hormoneFields = [
    { key: "Estradiol", label: "Estradiol", color: "#ff6384" },
    { key: "FollicleStimulatingHormone", label: "FSH", color: "#36a2eb" },
    { key: "LuteinizingHormone", label: "LH", color: "#cc65fe" },
    { key: "Progesterone", label: "Progesterone", color: "#ffce56" },
    { key: "Pregnancyhormone", label: "Pregnancy Hormone", color: "#2ecc40" },
    { key: "Antimullerianhormone", label: "AMH", color: "#ff8c00" },
    { key: "Antralfolliclecount", label: "Antral Follicle Count", color: "#008080" },
  ];
  const medicationFields = [
    { key: "OralMedicationDoseValue", label: "Oral Medication Dose", color: "#e6194b" },
    { key: "FollistimGonalfDose", label: "Follistim/Gonal-f Dose", color: "#3cb44b" },
    { key: "MenopurDose", label: "Menopur Dose", color: "#ffe119" },
    { key: "LeuprolideDose", label: "Leuprolide Dose", color: "#4363d8" },
    { key: "TriggerShotforOvulationValue", label: "Trigger Shot for Ovulation", color: "#f58231" },
    { key: "GanirelixCetrotideDose", label: "Ganirelix/Cetrotide Dose", color: "#911eb4" },
    { key: "Estradioldose", label: "Estradiol Dose", color: "#46f0f0" },
    { key: "ProgesteroneDose", label: "Progesterone Dose", color: "#f032e6" },
  ];

  // Whitelists for valid chart fields
  const validHormoneKeys = [
    "Estradiol",
    "FollicleStimulatingHormone",
    "LuteinizingHormone",
    "Progesterone",
    "Pregnancyhormone",
    "Antimullerianhormone",
    "Antralfolliclecount"
  ];
  const validMedicationKeys = [
    "OralMedicationDoseValue",
    "FollistimGonalfDose",
    "MenopurDose",
    "LeuprolideDose",
    "TriggerShotforOvulationValue",
    "GanirelixCetrotideDose",
    "Estradioldose",
    "ProgesteroneDose"
  ];

  const trendTabs = [
    { key: "Hormones", label: "Hormones" },
    { key: "Medications", label: "Medications" },
    { key: "BloodPressure", label: "Blood Pressure" },
    { key: "Glucose", label: "Glucose" },
    { key: "Weight", label: "Weight" },
  ];

  // Title mapping for each tab
  const tabTitles = {
    Hormones: 'Fertility Hormone Trends',
    Medications: 'Medication Trends',
    BloodPressure: 'Blood Pressure Trends',
    Glucose: 'Glucose Trends',
    Weight: 'Weight Trends',
  };

  // Style for active/inactive tab buttons to match screenshot
  const tabButtonStyle = (isActive) => ({
     background: isActive
      ? '#fff'
      : isHighmarkPortal
        ? '#0072CE'
        : '#f3e6ef',
    color: isActive
      ? isHighmarkPortal
        ? '#0072CE'
        : '#e6007a'
        : isHighmarkPortal
    ? '#fff'    // inactive + highmark
    : '#333',   // inactive + non-highmark
    border: isActive
      ? `2px solid ${isHighmarkPortal ? '#0072CE' : '#e6007a'}`
      : `2px solid ${isHighmarkPortal ? '#e6f3fa' : '#f3e6ef'}`,
    borderRadius: 24,
    fontWeight: 600,
    fontSize: 18,
    padding: '8px 28px',
    marginRight: 12,
    
    outline: 'none',
    boxShadow: isActive
      ? `0 2px 8px ${isHighmarkPortal ? '#e6f3fa' : '#f3e6ef'}`
      : 'none',
    transition: 'all 0.2s',
    cursor: 'pointer',
  });

  useEffect(() => {
    const params = { AppUserID: userData?.userData?.AppUserId };
    async function fetchData() {
      try {
        console.log("Trend API Request Params:", params);
        const response = await axios.post(
          Endpoints.LoadMiraDetails,
          `_Params=${encodeURIComponent(JSON.stringify(params))}`,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log("Trend API Response Data:", response.data);
        // Collect all unique field names from the API response
        let allFields = new Set();
        if (Array.isArray(response.data)) {
          response.data.forEach(item => {
            Object.keys(item).forEach(key => {
              if (!['SelectedDate', 'date', 'BloodPressure', 'Weight', 'Glucose', 'AppUserID'].includes(key)) {
                allFields.add(key);
              }
            });
          });
        }
        // Merge with predefined fields for legend and chart
        // Only use whitelisted fields, do not add any new fields from API
        // (removes dynamic push of unwanted fields)
        const hormoneKeys = hormoneFields.map(f => f.key);
        const medicationKeys = medicationFields.map(f => f.key);
        // Parse all valid dates and values from the API response
        const validData = Array.isArray(response.data) ? response.data.map((item) => {
            // Remove AppUserID from item for charting
            const { AppUserID, ...rest } = item;
            // Support multiple possible date field names
            let rawDate = rest.SelectedDate || rest.Date || rest.date || rest.TestDate || rest.ObservationDate || null;
            let date = null;
            if (rawDate) {
              try {
                date = parse(rawDate, "yyyy-MM-dd", new Date());
                if (isNaN(date.getTime())) {
                  date = parse(rawDate, "MM/dd/yyyy", new Date());
                }
                if (isNaN(date.getTime())) {
                  date = parse(rawDate, "M/d/yyyy", new Date());
                }
              } catch (e) {
                date = null;
              }
            }
            // Parse BP as number (systolic and diastolic)
            let bpSystolic = null;
            let bpDiastolic = null;
            if (rest.BloodPressure && typeof rest.BloodPressure === 'string' && rest.BloodPressure.includes('/')) {
              const [sys, dia] = rest.BloodPressure.split('/').map(Number);
              bpSystolic = Number(sys);
              bpDiastolic = Number(dia);
            } else if (!isNaN(Number(rest.BloodPressure))) {
              bpSystolic = Number(rest.BloodPressure);
            }
            // Parse weight
            let weight = null;
            if (
              (rest.Weightinlbs !== undefined && rest.Weightinlbs !== null && rest.Weightinlbs !== "" && !isNaN(Number(rest.Weightinlbs))) ||
              rest.Weightinlbs === null // treat null as 0
            ) {
              weight = rest.Weightinlbs === null ? 0 : Number(rest.Weightinlbs);
            } else if (rest.Weightinkgs !== undefined && rest.Weightinkgs !== null && rest.Weightinkgs !== "" && !isNaN(Number(rest.Weightinkgs))) {
              weight = Number(rest.Weightinkgs) * 2.20462;
            } else if (rest.Weight !== undefined && rest.Weight !== null && rest.Weight !== "" && !isNaN(Number(rest.Weight))) {
              weight = Number(rest.Weight);
            }
            // Parse glucose
            let glucose = !isNaN(Number(rest.Glucose)) ? Number(rest.Glucose) : null;

            // Fill missing hormone/medication fields with 0
            validHormoneKeys.forEach(key => {
              if (rest[key] === undefined || rest[key] === null || rest[key] === "") {
                rest[key] = 0;
              }
            });
            validMedicationKeys.forEach(key => {
              if (rest[key] === undefined || rest[key] === null || rest[key] === "") {
                rest[key] = 0;
              }
            });

            return {
              ...rest,
              date,
              BloodPressureSystolic: bpSystolic,
              BloodPressureDiastolic: bpDiastolic,
              BloodPressure: bpSystolic, // keep for backward compatibility
              Weight: weight,
              Glucose: glucose,
            };
          })
          .filter(item => item.date && !isNaN(item.date.getTime()))
          .sort((a, b) => a.date - b.date)
        : [];
        // Filter to latest 15 days only
        const sortedData = validData.sort((a, b) => b.date - a.date);
        const chartData = sortedData.slice(0, 15).reverse();
        // Ensure all hormone/medication fields are always present and numeric (default 0)
        chartData.forEach(row => {
          validHormoneKeys.forEach(key => {
            if (row[key] === undefined || row[key] === null || row[key] === "" || isNaN(Number(row[key]))) {
              row[key] = 0;
            } else {
              row[key] = Number(row[key]);
            }
          });
          validMedicationKeys.forEach(key => {
            if (row[key] === undefined || row[key] === null || row[key] === "" || isNaN(Number(row[key]))) {
              row[key] = 0;
            } else {
              row[key] = Number(row[key]);
            }
          });
        });
        // Debug: Log chartData and hormone/medication values for troubleshooting
        if (chartData.length > 0) {
          console.log("[Fertility_Trends] ChartData for ", activeTab, chartData);
          chartData.forEach((row, idx) => {
            console.log(`Date[${idx}]:`, row.date, "Hormones:", validHormoneKeys.map(k => row[k]), "Medications:", validMedicationKeys.map(k => row[k]));
          });
          setNoData(false);
          // Wait for DOM update, then check for chartDivRef
          setTimeout(() => {
            if (chartDivRef.current) {
              initializeChart(chartData, activeTab);
            } else {
              // Try again after a short delay if not present
              setTimeout(() => {
                if (chartDivRef.current) initializeChart(chartData, activeTab);
              }, 100);
            }
          }, 100);
        } else {
          setNoData(true);
        }
      } catch (error) {
        setNoData(true);
        let errorMsg = "Error loading trends data";
        if (error.response) {
          errorMsg += `: ${error.response.status} ${error.response.statusText}`;
          if (error.response.data) {
            errorMsg += `\nResponse: ${JSON.stringify(error.response.data)}`;
          }
        } else if (error.message) {
          errorMsg += `: ${error.message}`;
        }
        console.error("Trend Data Load Error:", error);
        setModalMessage(errorMsg);
        setShowModal(true);
      } finally {
        setLoading(false);
      }
    }
    function initializeChart(data, tab) {
      if (!chartDivRef.current) return; // Don't create chart if container not present
      if (am4core && am4core.disposeAllCharts) am4core.disposeAllCharts();
      // Prevent double chart creation on rapid tab switching
      if (!data || data.length === 0) return;
      const chart = am4core.create(chartDivRef.current, am4charts.XYChart);
      chart.paddingRight = 40;
      chart.paddingLeft = 40;
      chart.data = data;
      chart.logo.disabled = true;
      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.baseInterval = { timeUnit: "day", count: 1 };
      dateAxis.renderer.minGridDistance = 50;
      dateAxis.tooltipDateFormat = "MM/dd/yyyy";
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.min = 0;
      function createSeries(field, name, color) {
        const series = chart.series.push(new am4charts.LineSeries());
        series.dataFields.valueY = field;
        series.dataFields.dateX = "date";
        series.name = name;
        series.stroke = am4core.color(color);
        series.tooltipText = `${name}: [bold]{valueY}[/]`;
        series.strokeWidth = 2;
        series.tensionX = 2;
        series.fillOpacity = 0;
        const bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.circle.radius = 4;
        bullet.circle.fill = am4core.color("#fff");
        bullet.circle.strokeWidth = 2;
        bullet.circle.stroke = am4core.color(color);
        return series;
      }
      let filtered = [];
      if (tab === "Hormones") {
        // Always show all hormone fields, even if all values are 0
        filtered = hormoneFields.filter(f => validHormoneKeys.includes(f.key));
        filtered.forEach(f => createSeries(f.key, f.label, f.color));
      } else if (tab === "Medications") {
        // Always show all medication fields, even if all values are 0
        filtered = medicationFields.filter(f => validMedicationKeys.includes(f.key));
        filtered.forEach(f => createSeries(f.key, f.label, f.color));
      } else if (tab === "BloodPressure") {
        if (data.some(d => d.BloodPressureSystolic !== undefined && d.BloodPressureSystolic !== null && d.BloodPressureSystolic !== "")) {
          filtered = [
            { key: "BloodPressureSystolic", label: "Blood Pressure (Systolic)", color: "#d62728" },
            { key: "BloodPressureDiastolic", label: "Blood Pressure (Diastolic)", color: "#1f77b4" }
          ];
          createSeries("BloodPressureSystolic", "Blood Pressure (Systolic)", "#d62728");
          createSeries("BloodPressureDiastolic", "Blood Pressure (Diastolic)", "#1f77b4");
        }
      } else if (tab === "Glucose") {
        if (data.some(d => d.Glucose !== undefined && d.Glucose !== null && d.Glucose !== "")) {
          filtered = [{ key: "Glucose", label: "Glucose", color: "#17becf" }];
          createSeries("Glucose", "Glucose", "#17becf");
        }
      } else if (tab === "Weight") {
        if (data.some(d => d.Weight !== undefined && d.Weight !== null && d.Weight !== "")) {
          filtered = [{ key: "Weight", label: "Weight (lbs)", color: "#7f7f7f" }];
          createSeries("Weight", "Weight (lbs)", "#7f7f7f");
        }
      }
      setFilteredFields(filtered);
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.lineX.strokeWidth = 2;
      chart.cursor.lineY.strokeWidth = 0;
      chart.cursor.xAxis = dateAxis;
      chart.scrollbarX = new am4core.Scrollbar();
      chart.legend = new am4charts.Legend();
      chart.legend.position = "top";
      return () => {
        chart.dispose();
      };
    }
    fetchData();
    // re-render chart on tab change
    // eslint-disable-next-line
  }, [userData, token, activeTab, reloadKey]);

  const handleExport = async () => {
    setLoading(true);
    setExportLoading(true);
    try {
      const params = {
        EmailID: userData?.userData?.EmailID,
        MemberId : userData?.userData?.MemberID   ,
        Contract: userData?.userData?.Contract || userData?.userData?.ContractIdentified,
        IsExportType:"Fertility"
      };
      debugger
      const response = await axios.post(
        Endpoints.ExportPregnencyTrendReport,
        params,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data && response.data.Result === 1) {
        setModalMessage('Exported data successfully');
        setShowModal(true);
      } else {
        setModalMessage('Export failed. Please try again.');
        setShowModal(true);
      }
    } catch (error) {
      setModalMessage('Export failed. Please try again.');
      setShowModal(true);
    } finally {
      setExportLoading(false);
      setLoading(false);
      setReloadKey(prev => prev + 1);
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

   if (loading) {
    return (
      <div className="loading-container">
        <div className="loading"></div>        
        <h2 className="loading-text">{t("loading")}</h2>
      </div>
    );
  }

  return (
    <div>
      <div className="col-md-12 mt-4 d-flex inner-page-heading-wrapper">
        <button className="win-back-btn" onClick={handleBackClick}>
          <span>
            <i className="bi bi-chevron-left"></i>
            Back
          </span>
        </button>
        <h2 className="page-heading">{tabTitles[activeTab] }</h2>
      </div>
      <div className="trend-tabs" style={{ margin: '20px 0', display: 'flex', gap: 0, background: isHighmarkPortal ? "#0072CE":'#f3e6ef', borderRadius: 24, padding: 4, width: 'fit-content' }}>
        {trendTabs.map(tab => (
          <button
            key={tab.key}
            style={tabButtonStyle(activeTab === tab.key)}
            className={activeTab === tab.key ? "active" : ""}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="loading-container">
          <div className="loading"></div>
          <h2 className="loading-text">{t("loading")}</h2>
        </div>
      ) : noData ? (
        <p>{t('No data available')}</p>
      ) : (
        <>
          <div ref={chartDivRef} id="fertilityChartDiv" style={{ height: "500px" }}></div>
          <div style={{ textAlign: 'center', marginTop: 24, paddingBottom : 25 }}>
            <button
              onClick={handleExport}
              disabled={exportLoading}
              style={{
                background: isHighmarkPortal ? "#0072CE" : "#EC008C",
                color: '#fff',
                border: 'none',
                borderRadius: 24,
                padding: '12px 36px',
                fontWeight: 600,
                fontSize: 18,
                cursor: exportLoading ? 'not-allowed' : 'pointer',
                opacity: exportLoading ? 0.7 : 1,
                marginTop: 12,
              }}
            >
              {exportLoading ? 'Exporting...' : 'Export Report'}
            </button>
          </div>
        </>
      )}
      <ErrorModal
        show={showModal}
        message={modalMessage}
        onClose={() => setShowModal(false)}
      />
    </div>
  );
};

export default Fertility_Trends;
